/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Select, Switch } from "core/components";
import { AccountFormData, AccountSchema } from "core/schema";
import { admin_account_path, admin_accounts_path } from "routes";
import { navigateTo } from "core/navigation";
import { Option } from "core/types";

type AccountFormProps = {
  account: AccountFormData;
  account_subgroups: Array<Record<string, unknown>>;
  account_entry_types: Array<Option>;
};

const AccountForm = ({ account, account_subgroups, account_entry_types }: AccountFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<AccountFormData>({
    mode: "onChange",
    defaultValues: AccountSchema.cast(account),
    resolver: yupResolver(AccountSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: AccountFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_account_path(data), data);
      } else {
        await api.post(admin_accounts_path(), data);
      }

      navigateTo(admin_accounts_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={account.id ? "Edit Account" : "New Account"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_accounts_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Controller
                    name="account_subgroup_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="GL Account Subgroup"
                        placeholder="Select GL Account Subgroup"
                        required
                        options={account_subgroups}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        noOptionsMessage={() => "No GL Account Subgroups defined."}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={account_subgroups.find((account_subgroup) => account_subgroup.id == field.value) || null}
                        errorMessage={errors.account_subgroup_id?.message}
                      />
                    )}
                  />
                  <Controller
                    name="account_entry_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Account Entry Type"
                        placeholder="Select Account Entry Type"
                        isClearable={false}
                        required
                        options={account_entry_types}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        onChange={(newOption) => {
                          field.onChange(newOption?.value);
                        }}
                        value={account_entry_types?.find((account_entry_type) => account_entry_type.value == field.value) || null}
                        errorMessage={errors.account_entry_type?.message}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    className="field text-uppercase"
                    label="Code"
                    required
                    placeholder="Code"
                    errorMessage={errors.code?.message}
                    maxLength={17}
                  />
                  <Input {...register("name")} label="Name" required placeholder="Name" errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default AccountForm;
